import {
  CartesianGrid,
  Line,
  LineChart,
  ReferenceArea,
  ReferenceDot,
  ReferenceLine,
  ResponsiveContainer,
  Scatter,
  ScatterChart,
  XAxis,
  YAxis,
  ZAxis
} from 'recharts';
import { Neutral500, Neutral600 } from '@src/utils/colors';
import {
  BubbleColorMode,
  BubbleColorModeType,
  CAMPAIGN_BUBBLE_COLORS,
  CsGreen,
  CsRed,
  CsRedText,
  FATIGUE_THRESHOLD,
  FOCUS_LINES,
  FOCUS_PERF_MAX,
  QUADRANT_GROUPS,
  QUADRANT_MIDPOINT,
  SET_LINES,
  TIMELINE_DAYS
} from './creativeSuite-constants';

// Neutral300 (#e1e6ef) at 0.6 alpha — no matching alpha token exists in colors.ts.
const GRID_STROKE = 'rgba(225, 230, 239, 0.6)';
const TICK = { fill: Neutral500, fontSize: 10 };
const AXIS_TITLE = { fill: Neutral600, fontSize: 12 };
const lowHighTick = (v: number) => {
  if (v === 0) return 'Low';
  if (v === 1) return 'High';
  return '';
};

// A small upward triangle marking the day a new ad went live. ReferenceDot
// clones this element with the resolved cx/cy of the reference point.
interface LaunchMarkerProps {
  cx?: number;
  cy?: number;
  fill?: string;
}
const LaunchMarker = ({ cx = 0, cy = 0, fill = CsRed }: LaunchMarkerProps) => (
  <polygon points={`${cx},${cy - 9} ${cx - 5},${cy - 2} ${cx + 5},${cy - 2}`} fill={fill} />
);

// "Fatigue over time · by ad set" — one line per flagged ad set, a dashed
// threshold at 0.80, and a launch triangle where each set got a new ad.
export const SetLinesChart = () => {
  const data = TIMELINE_DAYS.map((day, i) => ({
    day,
    ...Object.fromEntries(SET_LINES.map(line => [line.key, line.values[i]]))
  }));

  return (
    <ResponsiveContainer width='100%' height='100%'>
      <LineChart data={data} margin={{ top: 12, right: 12, bottom: 0, left: -18 }}>
        <CartesianGrid stroke={GRID_STROKE} vertical={false} />
        <XAxis dataKey='day' tick={TICK} interval={4} axisLine={false} tickLine={false} />
        <YAxis
          domain={[0.2, 1]}
          ticks={[0.2, 0.4, 0.6, 0.8, 1]}
          tick={TICK}
          axisLine={false}
          tickLine={false}
        />
        <ReferenceLine
          y={FATIGUE_THRESHOLD}
          stroke={CsRed}
          strokeDasharray='4 3'
          strokeOpacity={0.55}
          label={{
            value: '0.80 fatigue threshold',
            position: 'insideTopLeft',
            fill: CsRedText,
            fontSize: 9
          }}
        />
        {SET_LINES.map(line => (
          <Line
            key={line.key}
            type='monotone'
            dataKey={line.key}
            stroke={line.color}
            strokeWidth={2}
            dot={false}
            isAnimationActive={false}
          />
        ))}
        {SET_LINES.map(line => (
          <ReferenceDot
            key={`launch-${line.key}`}
            x={TIMELINE_DAYS[line.launchDayIndex]}
            y={line.values[line.launchDayIndex]}
            r={0}
            shape={<LaunchMarker fill={line.color} />}
          />
        ))}
      </LineChart>
    </ResponsiveContainer>
  );
};

// Isolated ad set: fatigue rising + diversification falling on the 0–1 score
// axis, ROAS/CPA on the right performance axis.
export const FocusChart = () => {
  const data = TIMELINE_DAYS.map((day, i) => ({
    day,
    ...Object.fromEntries(FOCUS_LINES.map(line => [line.key, line.values[i]]))
  }));

  return (
    <ResponsiveContainer width='100%' height='100%'>
      <LineChart data={data} margin={{ top: 12, right: 4, bottom: 0, left: -18 }}>
        <CartesianGrid stroke={GRID_STROKE} vertical={false} />
        <XAxis dataKey='day' tick={TICK} interval={4} axisLine={false} tickLine={false} />
        <YAxis
          yAxisId='score'
          domain={[0, 1]}
          ticks={[0, 0.25, 0.5, 0.75, 1]}
          tick={TICK}
          axisLine={false}
          tickLine={false}
        />
        <YAxis
          yAxisId='perf'
          orientation='right'
          domain={[0, FOCUS_PERF_MAX]}
          tick={TICK}
          axisLine={false}
          tickLine={false}
        />
        {FOCUS_LINES.map(line => (
          <Line
            key={line.key}
            yAxisId={line.axis}
            type='monotone'
            dataKey={line.key}
            stroke={line.color}
            strokeWidth={2}
            strokeDasharray={line.dashed ? '5 4' : undefined}
            dot={false}
            isAnimationActive={false}
          />
        ))}
      </LineChart>
    </ResponsiveContainer>
  );
};

interface QuadrantBubbleChartProps {
  colorMode: BubbleColorModeType;
}

// Ad sets by diversification (x) and fatigue (y). Target zone is high
// diversification + low fatigue (bottom-right), where the healthy sets sit.
export const QuadrantBubbleChart = ({ colorMode }: QuadrantBubbleChartProps) => (
  <ResponsiveContainer width='100%' height='100%'>
    <ScatterChart margin={{ top: 16, right: 16, bottom: 8, left: 0 }}>
      <CartesianGrid stroke={GRID_STROKE} />
      <XAxis
        type='number'
        dataKey='x'
        domain={[0, 1]}
        ticks={[0, 1]}
        tickFormatter={lowHighTick}
        tick={TICK}
        label={{
          value: 'Diversification  →  (weighted higher)',
          position: 'bottom',
          style: AXIS_TITLE
        }}
      />
      <YAxis
        type='number'
        dataKey='y'
        domain={[0, 1]}
        ticks={[0, 1]}
        tickFormatter={lowHighTick}
        tick={TICK}
        label={{ value: 'Fatigue  →', angle: -90, position: 'insideLeft', style: AXIS_TITLE }}
      />
      <ZAxis type='number' dataKey='r' range={[60, 520]} />
      <ReferenceArea
        x1={QUADRANT_MIDPOINT}
        x2={1}
        y1={0}
        y2={QUADRANT_MIDPOINT}
        fill={CsGreen}
        fillOpacity={0.08}
        label={{
          value: '◎ Target zone',
          position: 'insideBottomRight',
          fill: CsGreen,
          fontSize: 11
        }}
      />
      <ReferenceArea
        x1={0}
        x2={QUADRANT_MIDPOINT}
        y1={QUADRANT_MIDPOINT}
        y2={1}
        fillOpacity={0}
        label={{
          value: 'Low diversity · High fatigue',
          position: 'insideTopLeft',
          fill: CsRed,
          fontSize: 11
        }}
      />
      <ReferenceLine
        x={QUADRANT_MIDPOINT}
        stroke={Neutral500}
        strokeDasharray='5 5'
        strokeOpacity={0.45}
      />
      <ReferenceLine
        y={QUADRANT_MIDPOINT}
        stroke={Neutral500}
        strokeDasharray='5 5'
        strokeOpacity={0.45}
      />
      {QUADRANT_GROUPS.map((group, i) => {
        const fill =
          colorMode === BubbleColorMode.CAMPAIGN ? CAMPAIGN_BUBBLE_COLORS[i] : group.color;
        return (
          <Scatter
            key={group.key}
            data={group.bubbles}
            fill={fill}
            fillOpacity={0.55}
            stroke={fill}
            isAnimationActive={false}
          />
        );
      })}
    </ScatterChart>
  </ResponsiveContainer>
);
